import axios from 'axios';
import { saveAs } from 'file-saver';

const BASE_URL = process.env.REACT_APP_API || '';
export const DEMO_PAYMENT_BLOCK_CODE = 4031;

export function getApiErrorCode(errorOrResponse) {
  return Number(errorOrResponse?.code ?? errorOrResponse?.response?.data?.code ?? errorOrResponse?.data?.code);
}

export function isDemoPaymentBlocked(errorOrResponse) {
  return getApiErrorCode(errorOrResponse) === DEMO_PAYMENT_BLOCK_CODE;
}

export function getDemoPaymentBlockMessage(errorOrResponse) {
  return errorOrResponse?.response?.data?.msg ?? errorOrResponse?.msg ?? errorOrResponse?.message ?? '시연 계정에서는 실제 결제나 취소를 진행할 수 없습니다.';
}

function normalizeApiError(error) {
  const payload = error?.response?.data;
  if (payload && payload.code !== undefined && payload.code !== null) {
    error.code = payload.code;
    error.data = payload.data;
    if (payload.msg) error.message = payload.msg;
  }
  return error;
}


/** 공통 응답: code 1000 = 성공, 그 외는 msg로 에러 처리 */
function handleResponse(res) {
  const { code, msg, data } = res.data ?? {};
  if (code !== 1000) {
    const error = new Error(msg || '오류가 발생했습니다.');
    error.code = code;
    error.data = data;
    error.response = res;
    throw error;
  }
  return data;
}

/**
 * API 요청 래퍼 (JSON, withCredentials로 쿠키 전송)
 */
export async function request({ method = 'GET', url, data, params, headers = {} }) {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
  try {
    const res = await axios({
      method,
      url: fullUrl,
      data,
      params,
      withCredentials: true,
      headers: { 'Content-Type': 'application/json', ...headers },
    });
    return handleResponse(res);
  } catch (error) {
    throw normalizeApiError(error);
  }
}

/**
 * FormData 업로드용 요청 (multipart/form-data, Content-Type은 브라우저가 boundary 포함해 설정)
 */
export async function requestForm({ method = 'POST', url, data, headers = {} }) {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
  try {
    const res = await axios({
      method,
      url: fullUrl,
      data,
      withCredentials: true,
      headers: data instanceof FormData
        ? { ...headers }
        : { 'Content-Type': 'application/json', ...headers },
    });
    return handleResponse(res);
  } catch (error) {
    throw normalizeApiError(error);
  }
}

/**
 * 파일 다운로드용 요청 (responseType: 'blob')
 * @returns {Promise<{ blob: Blob, filename?: string }>}
 */
export async function requestBlob({ method = 'GET', url, params }) {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
  const res = await axios({
    method,
    url: fullUrl,
    params,
    withCredentials: true,
    responseType: 'blob',
  });
  let filename;
  const disposition = res.headers?.['content-disposition'];
  if (disposition) {
    const encodedMatch = disposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
    const basicMatch = disposition.match(/filename\s*=\s*([^;]+)/i);
    const rawFilename = (encodedMatch?.[1] || basicMatch?.[1] || '')
      .trim()
      .replace(/^["']|["']$/g, '');
    if (rawFilename) {
      try {
        filename = decodeURIComponent(rawFilename);
      } catch {
        filename = rawFilename;
      }
    }
  }
  return { blob: res.data, filename };
}

async function getBlobErrorMessage(blob) {
  if (!blob || typeof blob.text !== 'function') return null;
  if (!String(blob.type || '').toLowerCase().includes('json')) return null;

  try {
    const payload = JSON.parse(await blob.text());
    return payload?.msg ?? payload?.message ?? payload?.error?.message ?? null;
  } catch (_) {
    return null;
  }
}

/** 서버가 반환한 파일명으로 Blob 응답을 저장한다. */
export async function downloadBlob({ method = 'GET', url, params, fallbackFilename }) {
  try {
    const { blob, filename } = await requestBlob({ method, url, params });
    const errorMessage = await getBlobErrorMessage(blob);
    if (errorMessage) throw new Error(errorMessage);
    saveAs(blob, filename || fallbackFilename || 'download');
  } catch (error) {
    const errorMessage = await getBlobErrorMessage(error?.response?.data);
    if (errorMessage) throw new Error(errorMessage);
    throw error;
  }
}

export { BASE_URL };
